import React, { useState } from "react";
import { toast } from "sonner";
import type { LoadingState, Reply, StoredSubmission } from "@/types/fitconnect";
import { getRepliesOfCase, refreshReplies } from "@/lib/fitconnect";
import { StoredSubmissionSelector } from "@/components/StoredSubmissionSelector.tsx";
import { useStoredSubmissions } from "@/hooks/useStoredSubmissions.ts";
import { ReplyList } from "@/components/ReplyList.tsx";
import { RotateCw } from "lucide-react";

function RefreshButton() {
  const [loading, setLoading] = useState(false);
  const refresh = async () => {
    setLoading(true);
    try {
      const response = await refreshReplies();
      if (response.error) {
        toast.error("Replies aktualisieren fehlgeschlagen", {
          description: response.error,
        });
      } else {
        toast.success(
          `${response.refreshedReplies} Replies wurden neu importiert`,
        );
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "unbekannter Fehler";
      toast.error("Replies aktualisieren fehlgeschlagen", {
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={() => {
        refresh();
      }}
      disabled={loading}
      className={`
        ${loading ? "pointer-events-none" : "cursor-pointer"} 
        ${loading ? "bg-gray-200" : "bg-gray-50"}
        inline-flex 
        h-10 w-10 
        items-center 
        justify-center 
        rounded-full 
        text-black 
        drop-shadow-sm
        transition-colors
        duration-150
        hover:bg-gray-200`}
      aria-label="Aktualisieren"
    >
      <RotateCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
    </button>
  );
}

const Replies = () => {
  const { listLoadingState, listResponseData } = useStoredSubmissions(true);
  const [detailLoadingState, setDetailLoadingState] =
    useState<LoadingState>("initial");
  const [detailResponseData, setDetailResponseData] = useState<Reply[]>([]);
  const [selectedSubmission, setSelectedSubmission] =
    useState<StoredSubmission | null>(null);

  const loadDetail = async (caseId: string) => {
    try {
      setDetailLoadingState("pending");

      const response = await getRepliesOfCase(caseId);

      setDetailResponseData(response.replies);
      setDetailLoadingState(response.error ? "error" : "success");

      if (response.error) {
        toast.error("Replies abfragen fehlgeschlagen", {
          description: response.error,
        });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "unbekannter Fehler";
      setDetailLoadingState("error");
      setDetailResponseData([]);
      toast.error("Replies abfragen fehlgeschlagen", {
        description: msg,
      });
    }
  };

  return (
    <main className="container">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">
          Replies <RefreshButton />
        </h1>
        <p className="text-base-content/70">
          Auflisten aller Replies (Antworten), die zum Case der Submission vom
          Fachverfahren gesendet wurden. Wählen Sie dazu eine Submission aus der
          Liste aus.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <StoredSubmissionSelector
          submissions={listResponseData}
          onSelect={(s) => {
            setSelectedSubmission(s);
            loadDetail(s.caseId);
          }}
          selectedId={
            selectedSubmission ? selectedSubmission.submissionId : null
          }
          isLoading={listLoadingState === "pending"}
        />
        <ReplyList
          eventLogEntries={detailResponseData}
          isLoading={detailLoadingState === "pending"}
        />
      </div>
    </main>
  );
};

export default Replies;
